/**
 * The business's own logo and name in the navigation.
 *
 * Reported as "the logo I uploaded doesn't update in the menu". It had never
 * been there: the rail showed Keystone's mark and the words "AJW Security"
 * typed into the source, so every business running this saw somebody else's
 * name above their own work.
 *
 * A first version read the organisation from the device store and refetched on
 * the row's `updated_at`. A browser pass showed neither half worked: the
 * organisation row is never in the working set, so the name was always empty,
 * and uploading a logo does not touch that row, so nothing ever refetched.
 * The server's own answer is used instead (the same `["org"]` query Settings
 * writes to on save, so an upload shows here without a reload), keyed on
 * `logo_updated_at`, which moves when the picture does.
 *
 * The logo sits behind the sign-in token, so it is fetched rather than linked.
 * The name is remembered for the next load so a tech with no signal still sees
 * their own business; the picture falls back to Keystone's mark.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api, readToken } from "../lib/api";
import { Mark } from "./Mark";

const NAME_KEY = "keystone.org-name";

function remembered(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? "";
  } catch {
    return "";
  }
}

function remember(name: string) {
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    // Private mode or blocked storage: the name just isn't remembered.
  }
}

export function OrgMark({ size = 30 }: { size?: number }) {
  const org = useQuery({ queryKey: ["org"], queryFn: () => api.org.get(), staleTime: 10_000, retry: false });
  const [src, setSrc] = useState<string | null>(null);
  const hasLogo = org.data?.has_logo ?? null;
  const stamp = String(org.data?.logo_updated_at ?? "");

  useEffect(() => {
    if (hasLogo === false) {
      setSrc(null);
      return;
    }
    if (hasLogo === null) return; // not heard from the server yet
    let url: string | null = null;
    let live = true;
    const token = readToken();
    void fetch("/v1/org/logo", { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (!live || !blob) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => {
        // No signal. The mark is the right answer.
      });
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [hasLogo, stamp]);

  const fresh = String(org.data?.trading_name || org.data?.name || "");
  useEffect(() => {
    if (fresh) remember(fresh);
  }, [fresh]);
  const name = fresh || remembered();

  return (
    <>
      {src ? <img src={src} alt="" className="brand-logo" style={{ height: size }} /> : <Mark size={size} />}
      <span>
        <b>Keystone</b>
        {name && <small>{name}</small>}
      </span>
    </>
  );
}
